"use client";

import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import RevealElement from "../revealElement";
import CreatorMarquee from "./CreatorMarquee";
import CreatorPassPreview from "./CreatorPassPreview";
import { useLenisContext } from "../provider/lenisProvider";

gsap.registerPlugin(ScrollTrigger);

const HOLD_STEP_INDEX = 2;
const HOLD_MULTIPLIER = 2.4;

function getSegmentWeights(numSteps: number) {
  return Array.from({ length: Math.max(numSteps - 1, 0) }, (_, i) =>
    i === HOLD_STEP_INDEX ? HOLD_MULTIPLIER : 1
  );
}

function getStepBoundaries(numSteps: number) {
  const weights = getSegmentWeights(numSteps);
  const total = weights.reduce((sum, w) => sum + w, 0) || 1;
  const boundaries = [0];
  let cumulative = 0;
  for (const w of weights) {
    cumulative += w;
    boundaries.push(cumulative / total);
  }
  return boundaries;
}

function locateSegment(value: number, boundaries: number[]) {
  let segment = boundaries.length - 2;
  for (let i = 0; i < boundaries.length - 1; i++) {
    if (value < boundaries[i + 1]) {
      segment = i;
      break;
    }
  }
  const segStart = boundaries[segment];
  const segEnd = boundaries[segment + 1];
  const localRaw = (value - segStart) / (segEnd - segStart || 1);
  return { segment, segStart, segEnd, localRaw };
}

type HowItWorksStep = {
  title: string;
  description: string;
};

type HowItWorksData = {
  eyebrow: string;
  heading: string;
  desc: string;
  steps: HowItWorksStep[];
};

type HowItWorksProps = {
  data?: Partial<HowItWorksData>;
};

const DEFAULT_DATA: HowItWorksData = {
  eyebrow: "How It Works",
  heading: "From Application to Paid Campaigns in 10 Campaigns.",
  desc: "Just tell us who you are, and we will match you with brands that fit.",
  steps: [
    {
      title: "Apply",
      description: "Fill in your profile. Takes about 10 minutes.",
    },
    {
      title: "Get Verified",
      description: "Our team reviews your application and confirms your creator profile.",
    },
    {
      title: "Get Matched",
      description: "We match you with brand campaigns that fit your audience and content style.",
    },
    {
      title: "Get Paid",
      description: "Collaborate on campaigns and get paid.",
    },
  ],
};

export default function HowItWorks({ data }: HowItWorksProps) {
  const content: HowItWorksData = { ...DEFAULT_DATA, ...data };
  const numSteps = content.steps.length;

  const sectionRef = useRef<HTMLDivElement>(null);
  const pinTargetRef = useRef<HTMLDivElement>(null);
  const counterRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<ScrollTrigger | null>(null);
  const programmaticTargetRef = useRef<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // True once the pinned scroll has moved past the final step, so the
  // "scroll to continue" hint can hide before the section unpins.
  const [pastLastStep, setPastLastStep] = useState(false);

  const { lenis } = useLenisContext();

  function goToStep(index: number) {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const boundaries = getStepBoundaries(numSteps);
    const progress = boundaries[index] ?? 0;
    const y = Math.round(trigger.start + progress * (trigger.end - trigger.start));

    // Let the snap callback know we're driving to an exact target so it
    // doesn't fight ScrollTrigger's own snap right after the jump.
    programmaticTargetRef.current = progress;
    window.setTimeout(() => {
      programmaticTargetRef.current = null;
    }, 200);

    // Jump instantly rather than animating the scroll. Animating means
    // ScrollTrigger has to scrub through several viewports of pinned
    // timeline in one go, which desyncs the pin (white gaps / flicker),
    // especially on mobile. An instant jump lands the pin in one update;
    // scrub:0.3 still eases the card + number change so it doesn't feel abrupt.
    if (lenis) {
      lenis.scrollTo(y, { immediate: true, force: true });
    } else {
      window.scrollTo(0, y);
    }
    ScrollTrigger.update();
  }

  function triggerScanAnimation() {
    const passEl = pinTargetRef.current?.querySelector<HTMLElement>(".pass");
    if (!passEl) return;
    passEl.classList.remove("scanning");
    void passEl.offsetWidth;
    passEl.classList.add("scanning");
  }

  useEffect(() => {
    if (!sectionRef.current || !pinTargetRef.current) return;

    const scrollDistancePerStep = () => window.innerHeight;
    const boundaries = getStepBoundaries(numSteps);
    const totalWeight = getSegmentWeights(numSteps).reduce((sum, w) => sum + w, 0) || 1;

    const trigger = ScrollTrigger.create({
      trigger: sectionRef.current,
      start: "top top",
      end: () => `+=${scrollDistancePerStep() * totalWeight}`,
      pin: pinTargetRef.current,
      pinSpacing: true,
      anticipatePin: 1,
      scrub: 0.3,
      snap: {
        snapTo: (value: number, self?: ScrollTrigger) => {
          if (programmaticTargetRef.current !== null) {
            return programmaticTargetRef.current;
          }
          const progress = self ? self.progress : value;
          const { segStart, segEnd, localRaw } = locateSegment(progress, boundaries);
          return localRaw >= 0.5 ? segEnd : segStart;
        },
        duration: { min: 0.15, max: 0.4 },
        ease: "power1.inOut",
      },
      onUpdate: (self) => {
        const { segment, localRaw } = locateSegment(self.progress, boundaries);
        const index = localRaw >= 0.5 ? segment + 1 : segment;
        setActiveIndex((prev) => (prev === index ? prev : index));

        const isPastLast = self.progress >= 0.985;
        setPastLastStep((prev) => (prev === isPastLast ? prev : isPastLast));
      },
    });

    triggerRef.current = trigger;

    return () => {
      trigger.kill();
      triggerRef.current = null;
    };
  }, [numSteps]);

  // Keep ScrollTrigger's pin in sync with Lenis' smoothed scroll. Without
  // this, a programmatic scrollTo makes the pinned panel visibly slip
  // (white gaps above/below) while Lenis animates.
  useEffect(() => {
    if (!lenis) return;
    const onScroll = () => ScrollTrigger.update();
    lenis.on("scroll", onScroll);
    return () => {
      lenis.off("scroll", onScroll);
    };
  }, [lenis]);

  useEffect(() => {
    if (activeIndex === 2) {
      triggerScanAnimation();
    } else {
      pinTargetRef.current?.querySelector<HTMLElement>(".pass")?.classList.remove("scanning");
    }
  }, [activeIndex]);

  return (
    <div className="relative bg-midGrey" ref={sectionRef}>
      <div
        ref={pinTargetRef}
        className="h-[100lvh] flex flex-col justify-between overflow-hidden"
      >
        <RevealElement className="pt-16 md:pt-24 px-7 sm:px-[10.417vw]">
          <p className="text-center text-purple font-mono uppercase tracking-[0.2em] body-small-fontsize mb-4">
            {content.eyebrow}
          </p>
          <h2 className="text-[#263244] text-center">{content.heading}</h2>
          <p className="text-center md:px-20">{content.desc}</p>
        </RevealElement>

        <div className="flex-1 min-h-0 flex items-start md:items-center pt-6 md:pt-0 sm:px-[10.417vw] px-7">
          <div className="flex lg:flex-row flex-col gap-5 md:gap-0 w-full md:items-center">
            <div className="flex flex-col md:justify-between lg:w-1/2 w-full">
              <div className="flex flex-row items-center md:gap-[2vw] gap-5">
                <div className="counter flex flex-col gap-y-2 justify-center" ref={counterRef}>
                  {content.steps.map((step, j) => (
                    <button
                      key={j}
                      type="button"
                      onClick={() => goToStep(j)}
                      aria-label={`Go to step ${j + 1}: ${step.title}`}
                      aria-current={j === activeIndex ? "step" : undefined}
                      className={`${
                        j === activeIndex ? "active" : ""
                      } counterBtn cursor-pointer transition-colors text-[12px] md:text-body-fontsize flex items-center justify-center md:w-10 w-8 h-auto aspect-square border-[1px] border-purple rounded-full leading-[1] text-purple`}
                    >
                      {j + 1}
                    </button>
                  ))}
                </div>
                <div className="inner">
                  <h3 className="title text-[33px] smallMobile:text-4xl font-bold md:text-font-85 text-purple">
                    {content.steps[activeIndex].title}
                  </h3>
                  <p className="description text-[#263244] leading-[1.3] mb-0">
                    {content.steps[activeIndex].description}
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:w-1/2 md:w-[75%] w-[80%] mx-auto flex items-center justify-center">
              <CreatorPassPreview index={activeIndex} />
            </div>
          </div>
        </div>

        <div
          aria-hidden="true"
          className={`scrollHint pointer-events-none flex flex-col items-center gap-1 pb-3 text-purple transition-opacity duration-300 ${
            pastLastStep ? "opacity-0" : "opacity-60"
          }`}
        >
          <span className="font-mono uppercase tracking-[0.2em] text-[10px] md:text-[11px]">
            Scroll to continue
          </span>
          <svg
            className="scrollHintArrow h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>

        <CreatorMarquee />
      </div>

      <style jsx>{`
        .counterBtn.active {
          background-color: #851bed;
          color: #ffffff;
        }
        .scrollHintArrow {
          animation: scrollHintBounce 1.8s ease-in-out infinite;
        }
        @keyframes scrollHintBounce {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(4px);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .scrollHintArrow {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
